import { mapCommonCreativeItemsToApi } from '../../AddCreativePage/AddCommonCreative/mappers';
import { CommonCreativeItem } from '../../AddCreativePage/typings';
import {
    ApiCropData,
    Bundle,
    CommonCreativeApiResponse,
    CommonCreativeIdeaApiResponse,
    CommonCreativePresetApiResponse,
    CreateCommonApiResponse,
    CreateCommonCreativeRequestData,
    CropFileApiResponse,
    DisclaimersApiResponse,
    FetchCommonFileDataResponse
} from '../../common/requestsTypings';
import { SharedData } from '../../common/typings';
import { ImageCropApiDataItem } from '../../CreativeImageCropModal/typings';
import { CreativeOptionsData } from '../../CreativeOptions/typings';
import { CanvasApiMethodExecutor } from '../CanvasApiMethodExecutor';

export class AddCommonCreativeMethodExecutor extends CanvasApiMethodExecutor {
    async fetchPresetData(id: number, sharedData?: SharedData): Promise<CommonCreativePresetApiResponse> {
        const path = `presets/${id}`;

        return await this.createRequest<CommonCreativePresetApiResponse>(path, {}, sharedData);
    }

    async fetchIdeaData(id: string, presetId: string, sharedData?: SharedData): Promise<CommonCreativeIdeaApiResponse> {
        const path = `ideas/${id}/presets/${presetId}`;

        return await this.createRequest<CommonCreativeIdeaApiResponse>(path, {}, sharedData);
    }

    async fetchBatchData(id: string, sharedData?: SharedData): Promise<CommonCreativeApiResponse> {
        const path = `creatives-batches/${id}/migrated`;

        return await this.createRequest<CommonCreativeApiResponse>(path, {}, sharedData);
    }

    async fetchDisclaimers(sharedData?: SharedData): Promise<DisclaimersApiResponse> {
        const path = `disclaimers`;

        return await this.createRequest<DisclaimersApiResponse>(path, {}, sharedData);
    }

    async fetchFileData(id: string, sharedData?: SharedData): Promise<FetchCommonFileDataResponse> {
        const path = `files/${id}`;

        return this.createRequest<FetchCommonFileDataResponse>(path, {}, sharedData);
    }

    async uploadFile(file: File, sharedData?: SharedData): Promise<FetchCommonFileDataResponse> {
        const path = 'files';

        const formData = new FormData();
        formData.append('attachment', file, file.name);

        const params = {
            method: 'POST',
            body: formData,
            headers: this.headersWithoutContentType
        };

        return await this.createRequest<FetchCommonFileDataResponse>(path, params, sharedData);
    }

    async uploadFileByLink(fileUrl: string, sharedData?: SharedData): Promise<FetchCommonFileDataResponse> {
        const path = 'files/url';

        const params = {
            method: 'POST',
            body: JSON.stringify({ fileUrl })
        };

        return await this.createRequest<FetchCommonFileDataResponse>(path, params, sharedData);
    }

    async cropFile(id: string, data: ImageCropApiDataItem[], sharedData?: SharedData): Promise<CropFileApiResponse> {
        const path = `files/${id}/cropped`;
        const sizes = data.map(item => item.size);

        const formData = new FormData();
        formData.append(
            'cropParameters',
            new Blob([JSON.stringify(sizes)], {
                type: 'application/json'
            })
        );
        data.forEach(item => formData.append('attachment', item.blob, 'croppedImage.png'));

        const params = {
            method: 'POST',
            body: formData,
            headers: this.headersWithoutContentType
        };

        return await this.createRequest<CropFileApiResponse>(path, params, sharedData);
    }

    async smartCenterFile(
        id: string,
        blob: Blob,
        crops: ApiCropData[],
        sharedData?: SharedData
    ): Promise<CropFileApiResponse> {
        const path = `files/${id}/cropped`;

        const formData = new FormData();
        formData.append(
            'cropParameters',
            new Blob([JSON.stringify(crops)], {
                type: 'application/json'
            })
        );
        formData.append('attachment', blob, 'croppedImage.png');

        const params = {
            method: 'POST',
            body: formData,
            headers: this.headersWithoutContentType
        };

        return await this.createRequest<CropFileApiResponse>(path, params, sharedData);
    }

    async createCreative(
        creativeOptions: CreativeOptionsData,
        bundle: Bundle,
        items: CommonCreativeItem[],
        presetId?: string,
        batchId?: string,
        sharedData?: SharedData,
        ideaId?: string,
        isAdaptive?: boolean
    ): Promise<CreateCommonApiResponse> {
        const path = 'creatives-batches';

        const requestData: CreateCommonCreativeRequestData = {
            items: mapCommonCreativeItemsToApi(
                creativeOptions.optionsValue,
                bundle,
                items,
                presetId,
                batchId,
                isAdaptive
            ),
            name: creativeOptions.name,
            path,
            ideaId
        };

        const params = {
            method: 'POST',
            body: JSON.stringify(requestData)
        };

        return await this.createRequest<CreateCommonApiResponse>(path, params, sharedData);
    }
}
